import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axiosInstance from '../../../utils/axios';
import { getRole } from '../../../utils/role';

const initialState = {
  bookings: [],       
  allBookings: [],    // For client-side filtering
  currentPage: 1,
  totalPages: 1,
  status: 'idle',
  error: null,
  currentRequestId: undefined,
};

// FETCH OWN BOOKINGS (Farmer / Field Officer / Partner / Support)
// For SUPPORT: server-side pagination with page/limit/keyword params
// For others: fetch all at once for client-side filtering/pagination
export const fetchMyBookings = createAsyncThunk(
  'bookings/fetchMyBookings',
  async ({ page = 1, limit = 15, keyword } = {}, { rejectWithValue }) => {
    try {
      const role = getRole();

      if (role === 'SUPPORT') {
        // Server-side pagination for Operations role
        let url = `/super-admin/samples?page=${page}&limit=${limit}`;
        if (keyword) url += `&keyword=${encodeURIComponent(keyword)}`;
        const { data } = await axiosInstance.get(url);
        return {
          mode: 'SUPPORT',
          bookings: data.bookings || [],
          totalCount: data.totalCount || 0,
          limit,
        };
      }

      // Client-side: fetch all at once
      let url = '';
      if (role === 'FARMER') {
        url = 'user/bookingsInfo';
      } else if (role === 'FIELD_OFFICER') {
        url = '/field-officer/bookings';
      } else if (role === 'PARTNER' || role === 'TEAM_MEMBER') {
        url = '/partner/bookings';
      } else {
        return { mode: 'CLIENT', bookings: [] };
      }

      const { data } = await axiosInstance.get(url);
      const bookings = Array.isArray(data)
        ? data
        : (data && Array.isArray(data.bookings) ? data.bookings : []);

      return { mode: 'CLIENT', bookings };

    } catch (err) {
      return rejectWithValue(err.response?.data?.msg || 'Failed to fetch bookings');
    }
  }
);

// FETCH USER BOOKINGS (Admin/Support)
export const fetchUserBookings = createAsyncThunk(
  'bookings/fetchUserBookings',
  async ({ page = 1, limit = 10, keyword, id }, { rejectWithValue }) => {
    try {
      let url = `super-admin/user-bookings/${id}?page=${page}&limit=${limit}`;
      if (keyword) url += `&keyword=${keyword}`;
      const { data } = await axiosInstance.get(url);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.msg || 'Failed to fetch bookings');
    }
  }
);

export const userBookingsSlice = createSlice({
  name: 'userBookings',
  initialState,
  reducers: {
    setCurrentPage(state, action) {
      state.currentPage = action.payload;
    },
    resetBookingsState(state) {
        state.bookings = [];
        state.allBookings = [];
        state.status = 'idle';
    }
  },
  extraReducers(builder) {
    builder
      // --- Fetch My Bookings (Client Side Pagination) ---
      .addCase(fetchMyBookings.pending, (state, action) => {
        state.status = 'loading';
        state.currentRequestId = action.meta.requestId;
      })
      .addCase(fetchMyBookings.fulfilled, (state, action) => {
        state.status = 'succeeded';
        if (state.currentRequestId !== action.meta.requestId) {
            return;
        }

        state.currentRequestId = undefined;
        const { mode, bookings, totalCount, limit } = action.payload;
        if (mode === 'SUPPORT') {
          // Server-side: store current page of results + total pages
          state.bookings = bookings;
          state.totalPages = Math.ceil((totalCount || 0) / (limit || 15));
        } else {
          // Client-side: store all data for local filtering/pagination
          state.allBookings = bookings;
          state.bookings = bookings;
        }
      })
      .addCase(fetchMyBookings.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
        if (state.currentRequestId !== action.meta.requestId) {
            return;
        }

        state.currentRequestId = undefined;
      })

      // Fetch User Bookings (Server Side Pagination)
      .addCase(fetchUserBookings.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchUserBookings.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const data = action.payload;
        if (Array.isArray(data)) {
             state.bookings = data;
             state.totalPages = 1;
        } else {
             state.bookings = data.bookings || [];
             state.totalPages = Math.ceil((data.totalCount || 0) / action.meta.arg.limit);
        }
      })
      .addCase(fetchUserBookings.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      });
  },
});

export const { setCurrentPage, resetBookingsState } = userBookingsSlice.actions;
export default userBookingsSlice.reducer;